import * as React from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router';

const API_URL = import.meta.env.VITE_API_URL;

const RECOMMENDED_SIZE = 512;
const MAX_DIMENSION = 800;
const MAX_SIZE_KB = 2048;

export default function MediaAdd() {
  const navigate = useNavigate();
  const [file, setFile] = React.useState<File | null>(null);
  const [preview, setPreview] = React.useState<string | null>(null);
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [imageWarning, setImageWarning] = React.useState<string | null>(null);
  const [imageError, setImageError] = React.useState<string | null>(null);
  const [titleError, setTitleError] = React.useState<string | null>(null);

  const isBlocked = !!imageError;

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;

    setImageError(null);
    setImageWarning(null);
    setFile(null);
    setPreview(null);

    if (f.size / 1024 > MAX_SIZE_KB) {
      setImageError(`Розмір файлу не повинен перевищувати ${MAX_SIZE_KB} КБ`);
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(f);

    img.onload = () => {
      const { width, height } = img;

      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        setImageError(
          `Розміри зображення ${width}×${height}px перевищують максимально допустимі ${MAX_DIMENSION}×${MAX_DIMENSION}px`,
        );
        URL.revokeObjectURL(objectUrl);
        return;
      }

      if (width !== RECOMMENDED_SIZE || height !== RECOMMENDED_SIZE) {
        setImageWarning(
          `Розміри зображення ${width}×${height}px. Рекомендовано ${RECOMMENDED_SIZE}×${RECOMMENDED_SIZE}px`,
        );
      }

      setFile(f);
      setPreview(objectUrl);
    };

    img.src = objectUrl;
  };

  const validate = () => {
    let valid = true;

    if (!title.trim()) {
      setTitleError('Назва є обовʼязковою');
      valid = false;
    } else {
      setTitleError(null);
    }

    if (!file) {
      if (!imageError) setImageError('Оберіть зображення');
      valid = false;
    }

    return valid;
  };

  const onSubmit = async () => {
    if (!validate() || !file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('description', description);

    const res = await fetch(`${API_URL}/media`, {
      method: 'POST',
      body: formData,
    });

    if (res.ok) navigate('/media');
  };

  return (
    <Box sx={{ mx: 10, mt: 4 }}>
      <Typography variant="h5" mb={2}>
        Додати медіа
      </Typography>

      <Button variant="contained" component="label">
        Завантажити файл
        <input
          hidden
          type="file"
          accept="image/*"
          multiple={false}
          onChange={onFileChange}
        />
      </Button>

      <Typography
        variant="caption"
        display="block"
        sx={{ mt: 1, color: 'text.secondary' }}
      >
        Рекомендоване зображення {RECOMMENDED_SIZE}×{RECOMMENDED_SIZE}px ·
        Максимум {MAX_DIMENSION}×{MAX_DIMENSION}px · До {MAX_SIZE_KB} КБ
      </Typography>

      {imageError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {imageError}
        </Alert>
      )}

      {imageWarning && !imageError && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {imageWarning}
        </Alert>
      )}

      {preview && file && (
        <Card sx={{ mt: 3 }}>
          <CardMedia
            component="img"
            image={preview}
            sx={{ width: 300, height: 300, objectFit: 'contain' }}
          />
          <CardContent>
            <Typography>{file.name}</Typography>
            <Typography variant="body2">
              {file.type || 'unknown'} · {(file.size / 1024).toFixed(2)} КБ
            </Typography>
          </CardContent>
        </Card>
      )}

      <TextField
        fullWidth
        label="Назва"
        sx={{ mt: 3 }}
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
          if (e.target.value.trim()) setTitleError(null);
        }}
        error={!!titleError}
        helperText={titleError}
      />

      <TextField
        fullWidth
        label="Опис"
        multiline
        rows={4}
        sx={{ mt: 3 }}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button variant="contained" onClick={onSubmit} disabled={isBlocked}>
          Зберегти
        </Button>
        <Button variant="outlined" onClick={() => navigate('/media')}>
          Скасувати
        </Button>
      </Box>
    </Box>
  );
}
